import authService, { TOKEN_TYPES } from "./authService";
import { toBackendTrialId } from "@/utils/trialIdMap";
import type {
  GenerateResult,
  RuleDefinition,
  RuleLevel,
  RunOutcome,
  TestResult,
} from "@/views/DataDiscrepancies/ruleStudio.types";

/** Client for the Rule Studio endpoints (`/data/trial/<id>/rule_studio/…`). */

const base = (trialId: string) =>
  `${import.meta.env.VITE_API_URL}/data/trial/${toBackendTrialId(trialId)}/rule_studio`;

async function request<T>(
  url: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  const response = await fetch(url, {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      payload && typeof payload.detail === "string"
        ? payload.detail
        : `Request failed (${response.status})`;
    throw new RuleStudioApiError(detail, response.status);
  }
  return payload as T;
}

export class RuleStudioApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "RuleStudioApiError";
    this.status = status;
  }
}

export type CreateRulePayload = {
  name: string;
  description?: string;
  level: RuleLevel;
  domain?: string;
  severity: string;
  sql_text: string;
  explanation?: string;
  activate: boolean;
};

const ruleStudioApi = {
  listRules: (trialId: string) =>
    request<RuleDefinition[]>(`${base(trialId)}/rules/`),

  getRule: (trialId: string, ruleId: number) =>
    request<RuleDefinition>(`${base(trialId)}/rules/${ruleId}/`),

  createRule: (trialId: string, payload: CreateRulePayload) =>
    request<RuleDefinition>(`${base(trialId)}/rules/`, {
      method: "POST",
      body: payload,
    }),

  patchRule: (
    trialId: string,
    ruleId: number,
    payload: Partial<CreateRulePayload> & { status?: string },
  ) =>
    request<RuleDefinition>(`${base(trialId)}/rules/${ruleId}/`, {
      method: "PATCH",
      body: payload,
    }),

  toggleRule: (trialId: string, ruleId: number, enabled: boolean) =>
    request<{ id: number; enabled_for_trial: boolean }>(
      `${base(trialId)}/rules/${ruleId}/toggle/`,
      { method: "POST", body: { enabled } },
    ),

  deleteRule: (trialId: string, ruleId: number) =>
    request<{
      deleted: boolean;
      name: string;
      discrepancies_retained: number;
    }>(`${base(trialId)}/rules/${ruleId}/`, { method: "DELETE" }),

  runRule: (trialId: string, ruleId: number) =>
    request<RunOutcome>(`${base(trialId)}/rules/${ruleId}/run/`, {
      method: "POST",
      body: {},
    }),

  generate: (
    trialId: string,
    payload: {
      prompt: string;
      level: RuleLevel;
      domain?: string;
      current_sql?: string;
      refinement?: string;
    },
  ) =>
    request<GenerateResult>(`${base(trialId)}/generate/`, {
      method: "POST",
      body: payload,
    }),

  testSql: (trialId: string, payload: { sql_text: string; level: RuleLevel }) =>
    request<TestResult>(`${base(trialId)}/test/`, {
      method: "POST",
      body: payload,
    }),
};

export default ruleStudioApi;
